// types.ts
declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
  }
}
export type CallDirection = 'incoming' | 'outgoing';
export type RingtoneType = 'incoming' | 'outgoing' | 'classic' | 'modern';

interface RingtoneOptions {
  volume?: number;
  loop?: boolean;
  callDirection?: CallDirection;
}

// ringtoneGenerator.ts
class RingtoneGenerator {
  private audioContext: AudioContext | null = null;
  private gainNode: GainNode | null = null;
  private oscillators: OscillatorNode[] = [];
  private isPlaying = false;

  constructor() {
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioContext = new AudioCtx();
      this.gainNode = this.audioContext.createGain();
      this.gainNode.connect(this.audioContext.destination);
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }

  private async createTone(
    freq: number,
    duration: number,
    delay = 0,
  ): Promise<void> {
    if (!this.audioContext || !this.gainNode || !this.isPlaying) return;

    return new Promise(resolve => {
      setTimeout(() => {
        if (!this.isPlaying) return resolve();

        const osc = this.audioContext!.createOscillator();
        const env = this.audioContext!.createGain();

        osc.connect(env).connect(this.gainNode!);
        osc.frequency.value = freq;
        osc.type = 'sine';

        const now = this.audioContext!.currentTime;
        env.gain.setValueAtTime(0, now);
        env.gain.linearRampToValueAtTime(0.1, now + 0.05);
        env.gain.linearRampToValueAtTime(0, now + duration);

        osc.start(now);
        osc.stop(now + duration);
        this.oscillators.push(osc);

        setTimeout(() => {
          this.oscillators = this.oscillators.filter(o => o !== osc);
          resolve();
        }, duration * 1000);
      }, delay);
    });
  }

  private async playPattern(pattern: () => Promise<void>): Promise<void> {
    if (!this.audioContext) throw new Error('Audio context not available');
    this.isPlaying = true;
    try {
      while (this.isPlaying) await pattern();
    } catch (error) {
      console.warn('Error playing ringtone:', error);
    }
  }

  async play(type: RingtoneType): Promise<void> {
    const patterns = {
      incoming: async () => {
        await Promise.all([
          this.createTone(440, 0.4),
          this.createTone(880, 0.4),
        ]);
        if (!this.isPlaying) return;
        await this.delay(200);
        await Promise.all([
          this.createTone(440, 0.4),
          this.createTone(880, 0.4),
        ]);
        if (!this.isPlaying) return;
        await this.delay(2000);
      },
      outgoing: async () => {
        await this.createTone(400, 0.8);
        if (!this.isPlaying) return;
        await this.delay(3000);
      },
      classic: async () => patterns.incoming(),
      modern: async () => {
        const notes = [261.63, 293.66, 329.63, 349.23];
        for (const note of notes) {
          if (!this.isPlaying) break;
          await this.createTone(note, 0.3);
          await this.delay(100);
        }
        if (!this.isPlaying) return;
        await this.delay(1500);
      },
    };

    await this.playPattern(patterns[type]);
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  stop(): void {
    this.isPlaying = false;
    this.oscillators.forEach(osc => {
      try {
        osc.stop();
      } catch (_error) {
        // Ignore errors when stopping oscillators
        // This can happen if the oscillator is already stopped
      }
    });
    this.oscillators = [];
  }

  setVolume(volume: number): void {
    if (this.gainNode) {
      this.gainNode.gain.setValueAtTime(
        Math.max(0, Math.min(1, volume)),
        this.audioContext?.currentTime || 0,
      );
    }
  }

  getIsPlaying(): boolean {
    return this.isPlaying;
  }
}

// ringtoneManager.ts
class RingtoneManager {
  private audio: HTMLAudioElement | null = null;
  private isPlaying = false;
  private useFallback = false;
  private stopped = false;
  private generator = new RingtoneGenerator();

  constructor(private audioSrc: string) {
    this.initializeAudio();
  }

  private initializeAudio() {
    try {
      this.audio = new Audio(this.audioSrc);
      this.audio.volume = 0.8;
      this.audio.loop = true;
      this.audio.preload = 'auto';

      this.audio.addEventListener('error', () => {
        console.warn(`Audio file ${this.audioSrc} not found, using fallback`);
        this.useFallback = true;
      });
    } catch (error) {
      console.warn('Failed to initialize audio:', error);
      this.useFallback = true;
    }
  }

  async play(options: RingtoneOptions = {}): Promise<boolean> {
    const { volume = 0.8, loop = true, callDirection = 'incoming' } = options;
    this.stopped = false;

    try {
      if (this.useFallback) {
        this.generator.setVolume(volume);
        await this.generator.play(callDirection);
        this.isPlaying = true;
        return true;
      }

      if (!this.audio) return false;

      this.audio.volume = Math.max(0, Math.min(1, volume));
      this.audio.loop = loop;
      this.audio.currentTime = 0;

      await this.audio.play();
      this.isPlaying = true;
      return true;
    } catch (_error) {
      if (!this.useFallback && !this.stopped) {
        this.useFallback = true;
        return this.play(options);
      }
      return false;
    }
  }

  stop(): boolean {
    this.stopped = true;
    try {
      if (this.useFallback) {
        this.generator.stop();
      } else if (this.audio) {
        this.audio.pause();
        this.audio.currentTime = 0;
      }
      this.isPlaying = false;
      return true;
    } catch {
      return false;
    }
  }

  setVolume(volume: number): void {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    if (this.useFallback) {
      this.generator.setVolume(clampedVolume);
    } else if (this.audio) {
      this.audio.volume = clampedVolume;
    }
  }

  getIsPlaying(): boolean {
    return this.isPlaying;
  }
}

// Global state management
class RingtoneController {
  private managers = {
    incoming: new RingtoneManager('/files/whatsapp_incoming_call.mp3'),
    outgoing: new RingtoneManager('/files/whatsapp_outgoing_call.mp3'),
  };

  private currentDirection: CallDirection | null = null;

  async playRingtone(
    direction: CallDirection,
    options?: Omit<RingtoneOptions, 'callDirection'>,
  ): Promise<boolean> {
    // Stop current ringtone if switching direction
    if (this.currentDirection && this.currentDirection !== direction) {
      console.log(
        `Switching from ${this.currentDirection} to ${direction} ringtone`,
      );
      this.stopRingtone(this.currentDirection);
    }

    // Stop the same direction if already playing (restart)
    if (this.currentDirection === direction) {
      this.stopRingtone(direction);
    }

    this.currentDirection = direction;
    const success = await this.managers[direction].play({
      ...options,
      callDirection: direction,
    });

    if (!success) {
      this.currentDirection = null;
    }

    return success;
  }

  stopRingtone(direction: CallDirection): boolean {
    const success = this.managers[direction].stop();
    if (this.currentDirection === direction) {
      this.currentDirection = null;
    }
    return success;
  }

  stopAllRingtones(): boolean {
    const success = Object.values(this.managers).every(m => m.stop());
    this.currentDirection = null;
    return success;
  }

  setRingtoneVolume(volume: number): void {
    Object.values(this.managers).forEach(m => m.setVolume(volume));
  }

  isRingtonePlaying(): boolean {
    return Object.values(this.managers).some(m => m.getIsPlaying());
  }

  getCurrentDirection(): CallDirection | null {
    return this.currentDirection;
  }

  getManager(direction: CallDirection): RingtoneManager {
    return this.managers[direction];
  }
}

// Lazy singleton controller - only created when first needed
let ringtoneController: RingtoneController | null = null;

const getController = (): RingtoneController => {
  if (!ringtoneController) {
    ringtoneController = new RingtoneController();
  }
  return ringtoneController;
};

export default RingtoneManager;

// Simplified API that handles switching automatically
export const playRingtone = (
  direction: CallDirection,
  options?: Omit<RingtoneOptions, 'callDirection'>,
) => getController().playRingtone(direction, options);

export const stopRingtone = (direction: CallDirection) =>
  getController().stopRingtone(direction);

export const stopAllRingtones = () => getController().stopAllRingtones();

export const setRingtoneVolume = (volume: number) =>
  getController().setRingtoneVolume(volume);

export const isRingtonePlaying = () => getController().isRingtonePlaying();

export const getCurrentRingtoneDirection = () =>
  getController().getCurrentDirection();

export const getRingtoneManager = (direction: CallDirection) =>
  getController().getManager(direction);
